import { useCallback, useState } from 'react';
import { StripeError } from '@stripe/stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { Banner } from '@cbhq/cds-web/banner/Banner';
import { VStack } from '@cbhq/cds-web/layout';
import { useToast } from '@cbhq/cds-web/overlays/useToast';

import { QueryKey } from ':cloud/queries/types';
import { AppRoute } from ':cloud/utils/routes';
import { base64UrlEncode } from ':cloud/utils/serialization';
import {
  PaymentMethodIntake,
  PaymentMethodIntakeProps,
} from ':cloud/widgets/billing/PaymentMethodIntake';
import { FieldCopy } from ':cloud/widgets/waasProjects/projectFormFields/FieldCopy';

type PaymentError = { errorMessage?: string; error: StripeError };

type ProjectPaymentIntakeProps = {
  onCancel?: () => void;
  formContext?: Record<string, unknown>;
  onSuccess?: () => void;
  onError?: () => void;
  onProcessing?: () => void;
  onRequiresPaymentMethod?: () => void;
} & Pick<PaymentMethodIntakeProps, 'submitActionLabel' | 'cancelActionLabel'>;

export function ProjectPaymentIntake({
  submitActionLabel = 'Save',
  cancelActionLabel = 'Back',
  formContext = {},
  onCancel,
  onSuccess,
  onError,
  onProcessing,
  onRequiresPaymentMethod,
}: ProjectPaymentIntakeProps) {
  const toast = useToast();
  const [isStripeLoading, setIsStripeLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<
    { errorMessage: string | undefined; error: StripeError } | undefined | PaymentError
  >();
  const queryClient = useQueryClient();

  const onStripeProcessing = useCallback((): void => {
    setIsStripeLoading(false);
    onProcessing?.();
  }, [onProcessing]);

  const onStripeSucceeded = useCallback(async (): Promise<void> => {
    setIsStripeLoading(false);
    await queryClient.invalidateQueries([QueryKey.billing, 'payment-method']);
    toast.show('Payment method saved.');
    onCancel?.();
    onSuccess?.();
  }, [toast, onCancel, queryClient, onSuccess]);

  const onStripeError = useCallback(
    (errorMessage: string | undefined, rawError: StripeError): void => {
      setIsStripeLoading(false);
      setPaymentError({
        errorMessage,
        error: rawError,
      });
      onError?.();
    },
    [onError],
  );

  const onStripeRequiresPaymentMethod = useCallback(() => {
    setIsStripeLoading(false);
    onRequiresPaymentMethod?.();
  }, [onRequiresPaymentMethod]);

  return (
    <div>
      <FieldCopy
        title="Add a credit card"
        description="The account on file will be charged monthly. Your information will be securely saved."
      />
      <VStack width="100%" aria-label="Payment method">
        {paymentError && (
          <VStack>
            <Banner
              title="An error occurred processing your payment method"
              variant="warning"
              startIcon="error"
            >
              {paymentError.errorMessage || 'Unknown error'}
            </Banner>
          </VStack>
        )}
        <PaymentMethodIntake
          onSuccess={onStripeSucceeded}
          onProcessing={onStripeProcessing}
          onError={onStripeError}
          clearError={() => setPaymentError(undefined)}
          onRequiresPaymentMethod={onStripeRequiresPaymentMethod}
          parentComponentName="ProjectPayment"
          context={base64UrlEncode(JSON.stringify(formContext))}
          returnRoute={AppRoute.Projects.New}
          showDisclaimer={false}
          submitActionLabel={submitActionLabel}
          cancelActionLabel={cancelActionLabel}
          onCancel={onCancel}
          isLoading={isStripeLoading}
        />
      </VStack>
    </div>
  );
}
